import React from 'react';
import Box from '@mui/material/Box';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import FolderCopy from '@mui/icons-material/FolderCopy';
import TableView from '@mui/icons-material/TableView';
import { ExportMode } from '../../types/export';

interface OutputSummaryProps {
  mode: ExportMode;
  onModeChange: (mode: ExportMode) => void;
  /** Singular record noun, e.g. "fulfillment context". */
  noun: string;
  /** Workbook tabs in consolidated mode: the export type first, then included related data. */
  tabs: string[];
}

const toggleSx = { height: 32, px: 1.5, fontSize: 13, fontWeight: 600, textTransform: 'none', whiteSpace: 'nowrap' } as const;

/** Hidden for now: exports are always a single workbook. Flip to true to let users pick a file per record again. */
const MODE_TOGGLE_ENABLED = false;

/** Describes the files the job will produce, and switches between consolidated and detailed output. */
const OutputSummary: React.FC<OutputSummaryProps> = ({ mode, onModeChange, noun, tabs }) => {
  const consolidated = mode === ExportMode.CONSOLIDATED;
  const Icon = consolidated ? TableView : FolderCopy;
  const detail = consolidated
    ? tabs.length > 1
      ? `${tabs.length} tabs: ${tabs.join(', ')}`
      : `A single tab with ${tabs[0]?.toLowerCase() ?? 'the exported records'}.`
    : `Creates a separate file for each ${noun}.`;

  return (
    <Box
      sx={(theme) => ({
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 1.75,
        px: 2.5,
        py: 2,
        borderRadius: 4,
        bgcolor: alpha(theme.palette.primary.main, 0.06),
        border: 1,
        borderColor: alpha(theme.palette.primary.main, 0.25),
      })}
    >
      <Box aria-hidden sx={{ width: 40, height: 40, flexShrink: 0, borderRadius: 3, display: 'grid', placeItems: 'center', bgcolor: 'brand.container', color: 'brand.onContainer' }}>
        <Icon sx={{ fontSize: 22 }} />
      </Box>
      <Box sx={{ flex: '1 1 240px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{consolidated ? "You'll get one Excel workbook" : `You'll get one file per ${noun}`}</Typography>
        <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{detail}</Typography>
      </Box>
      {MODE_TOGGLE_ENABLED && (
        <ToggleButtonGroup
          exclusive
          size="small"
          value={mode}
          aria-label="Export mode"
          onChange={(_, next: ExportMode | null) => next && onModeChange(next)}
          sx={{ bgcolor: 'background.paper', '& .MuiToggleButton-root.Mui-selected': { bgcolor: 'brand.container', color: 'brand.onContainer' } }}
        >
          <ToggleButton value={ExportMode.CONSOLIDATED} sx={toggleSx}>
            Single workbook
          </ToggleButton>
          <ToggleButton value={ExportMode.DETAILED} sx={toggleSx}>
            File per {noun}
          </ToggleButton>
        </ToggleButtonGroup>
      )}
    </Box>
  );
};

export default OutputSummary;
